import * as Sentry from '@sentry/nextjs';
import {takeLatest, put} from 'redux-saga/effects';
import {AuthUser} from '../types/auth';
import * as actions from '../actions/authActions';
import * as authActionTypes from '../actions/authActionTypes';
import {decode} from 'jsonwebtoken';
import {getUserIdFromCookie} from 'lib/cookies';

const QUOTES_URL = process.env.NEXT_PUBLIC_QUOTES_URL || 'https://quotes.beamplify.com';

// TODO: Refactor these two conditions down to reduce repetition
function* authenticate(action: Action) {
    try {
        const user: AuthUser = action.payload ? yield authenticateViaToken(action) : yield authenticateViaCookie();
        yield put(actions.authenticateSuccess(user));
    } catch (e) {
        console.error(e);
        yield put(actions.authenticateFailed((e as Error).message));
    }
}

function* authenticateViaToken(action: Action) {
    yield put(actions.authenticateViaToken());

    const valid: boolean = yield acquireTokensAPI(action.payload);

    if (valid) {
        const user: AuthUser = yield getUserAPI();

        if (user) {
            yield put(actions.authenticateViaTokenSuccess(user));
            return user;
        }
    }

    yield put(actions.authenticateViaTokenFailed());
    throw new Error('Authentication failed via token');
}

function* authenticateViaCookie() {
    const user: AuthUser = yield getUserAPI();

    if (user) {
        yield put(actions.authenticateViaCookieSuccess(user));
        return user;
    }
    yield put(actions.authenticateViaCookieFailed());
    throw new Error('Authentication failed via cookie');
}

function* authenticateViaLoginless(action: Action) {
    if (action.payload) {
        // attempt normal auth but don't update state if failure
        try {
            const user: AuthUser = action.payload.vt
                ? yield authenticateViaToken(action)
                : yield authenticateViaCookie();

            yield put(actions.authenticateSuccess(user));
            return;
        } catch (e) {
            // we don't need to do anything here since an error is expected to proceed with loginless
            // but must catch to complete execution of saga
        } finally {
            const llt: string = action.payload.llt;
            const scope: string = action.payload.scope;

            if (llt && scope) {
                const verified: boolean = yield verifyLoginlessTokenAPI(llt, scope);

                if (verified) {
                    const email: string = yield getEmailFromToken(llt);
                    const user: AuthUser = yield getUserLoginlessAPI(email, scope);

                    if (user) {
                        yield put(actions.authenticateViaLoginlessSuccess(user, llt, scope));
                        return;
                    }
                }
            }
        }
    }

    yield put(actions.authenticateViaLoginlessFailed('Authentication via single-use token failed'));
}

function* login(action: Action<string>) {
    try {
        const success: boolean = yield loginAPI(action.payload);
        const cookie = getUserIdFromCookie();

        if (success) {
            yield put(actions.loginSuccess('Please check your email to complete your login.'));
            return;
        }
        if (cookie) {
            throw new Error(
                `Sorry, we're unable to find that email address in our system. <a href='${QUOTES_URL}'>Please resume your application here</a>`,
            );
        }
        throw new Error(
            `Hi, let's find a product that fits your needs. <a href='${QUOTES_URL}'>Please feel free to restart your application here</a>.`,
        );
    } catch (e: any) {
        yield put(actions.loginFailed(e.message));
    }
}

function* logout() {
    try {
        yield logoutAPI();
        clearSentryAuthContext();
        yield put(actions.logoutSuccess());
    } catch (e) {
        yield put(actions.logoutFailed());
    }
}

function setSentryAuthContext({payload}: Action<AuthUser | {user: AuthUser}>) {
    const user = 'user' in payload ? payload.user : payload;
    Sentry.setUser({
        id: user.id,
        email: user.email,
    });
}

function clearSentryAuthContext() {
    Sentry.setUser(null);
}

async function request(...args: Parameters<typeof fetch>) {
    const [path, options = {}] = args;
    return fetch(path, {
        ...options,
        headers: {
            ...options.headers,
            'Content-Type': 'application/json',
        },
    });
}

async function loginAPI(email: string) {
    const res = await request('/api/login/', {
        method: 'POST',
        body: JSON.stringify({
            email,
        }),
    });

    try {
        const data = await res.json();
        return res.status === 200 && data.status === 'success';
    } catch (e) {
        return false;
    }
}

async function getUserAPI() {
    const res = await request('/api/getUser/', {
        method: 'POST',
    });

    if (res.status === 200) {
        return res.json();
    }

    return null;
}

async function getUserLoginlessAPI(email: string | null, scope: string) {
    if (email == null) return;

    const res = await request('/api/getUser/loginless', {
        method: 'POST',
        body: JSON.stringify({
            email,
            scope,
        }),
    });

    if (res.status === 200) {
        return res.json();
    }

    return null;
}

async function logoutAPI() {
    return request('/api/logout/');
}

async function acquireTokensAPI(vt: string) {
    const res = await request('/api/acquireTokens/', {
        method: 'POST',
        body: JSON.stringify({vt}),
    });

    if (res.status === 200) {
        const data = await res.json();
        return data.status === 'success';
    }

    return false;
}

async function verifyLoginlessTokenAPI(llt: string, scope: string) {
    const res = await request('/api/verifyToken/loginless', {
        method: 'POST',
        body: JSON.stringify({llt: llt, scope: scope}),
    });

    return res.status === 200;
}

function getEmailFromToken(llt: string) {
    return (decode(llt) as any).email;
}

export default function* authSaga() {
    yield takeLatest(authActionTypes.AUTH_AUTHENTICATE_VIA_LOGINLESS, authenticateViaLoginless);
    yield takeLatest(authActionTypes.AUTH_AUTHENTICATE, authenticate);
    yield takeLatest(authActionTypes.AUTH_LOGIN, login);
    yield takeLatest(authActionTypes.AUTH_LOGOUT, logout);
    yield takeLatest(
        [authActionTypes.AUTH_AUTHENTICATE_SUCCESS, authActionTypes.AUTH_AUTHENTICATE_VIA_LOGINLESS_SUCCESS],
        setSentryAuthContext,
    );
    yield takeLatest(
        [authActionTypes.AUTH_AUTHENTICATE_FAILED, authActionTypes.AUTH_AUTHENTICATE_VIA_LOGINLESS_FAILED],
        clearSentryAuthContext,
    );
}
